import { AnimationController } from './AnimationController';
import { AnimationModeConfig } from './AnimationTypes';

export function bindAnimationDOM(
    controller: AnimationController,
    uniqueId: number,
    activePlaybackMode: AnimationModeConfig,
): () => void {
    // Array to collect dynamic loop listener cleanups (Popups & Bottom Sheet)
    const dynamicCleanups: Array<() => void> = [];

    // Phase 1: Dynamic Discovery & Global Initializations
    controller.cacheDOMElements();
    controller.startTrackerInterval();

    const initialAutoOption = controller.qualityPopup?.querySelector(
        '[data-value="auto"]',
    );
    if (initialAutoOption) {
        initialAutoOption.classList.add('isActive');
    }

    // Phase 2: Configuration Sub-Popup Option Bindings (Desktop)
    controller.setupPopupOptions(controller.focusPopup, (val) => {
        const activeIndex = Number(val);
        controller.engine.setAlignmentIndex(activeIndex);
        const textNode =
            controller.focusBtn?.querySelector('.Animation_btnText');
        const optionNode = controller.focusPopup?.querySelector(
            `[data-value="${val}"]`,
        );
        if (textNode && optionNode) {
            textNode.textContent = optionNode.textContent?.trim() || '';
        }
    });

    const onFocusClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.togglePopup(controller.focusPopup, controller.qualityPopup);
    };
    controller.focusBtn?.addEventListener('click', onFocusClick);

    controller.setupPopupOptions(controller.fpsPopup, (val) => {
        const targetFps =
            val === 'auto' ? activePlaybackMode.defaultFps : Number(val);
        controller.engine.updateFps(targetFps);
        const textNode = controller.fpsBtn?.querySelector('.Animation_btnText');
        if (textNode) textNode.textContent = `${targetFps} FPS`;
    });

    controller.setupPopupOptions(controller.qualityPopup, (val) => {
        let resolvedSize = val;
        if (val === 'auto')
            resolvedSize = activePlaybackMode.defaultSize || 'm';

        controller.engine.changeResolution(resolvedSize);

        // Exact Spot: Trigger the centralized tracking synchronizer
        controller.syncPlaybackPostResolutionChange(
            activePlaybackMode.modeType,
        );
    });

    // Phase 3: Pure Event Interceptions Setup
    const onPlayClick = (e: MouseEvent) => {
        e.stopPropagation();
        // Guard: Prevent interaction with invisible buttons on touch screens
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.handleToggle();
    };

    const onMobilePlayOverlayClick = (e: MouseEvent) => {
        e.stopPropagation();
        controller.handleToggle();
        controller.container?.classList.remove('isHudVisible');
        if (
            controller.progressBar &&
            controller.progressBar.classList.contains('isLoaded')
        ) {
            controller.progressBar.classList.remove('isVisible');
        }
    };

    const onPrevClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.handlePrev();
    };

    const onNextClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.handleNext();
    };

    const onFpsClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.togglePopup(controller.fpsPopup, controller.qualityPopup);
    };

    const onQualityClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.togglePopup(controller.qualityPopup, controller.fpsPopup);
    };

    const onMetaClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.handleCopy();
    };

    // Mobile UI Interception Events Handlers
    const onBSTriggerClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            !(controller as any).hasTrueHover &&
            !controller.container?.classList.contains('isHudVisible')
        )
            return;
        controller.openBottomSheet();
    };

    const onBSCloseClick = (e: MouseEvent) => {
        e.stopPropagation();
        controller.closeBottomSheet();
    };

    // Binding standard control interfaces
    controller.playBtn?.addEventListener('click', onPlayClick);
    controller.prevBtn?.addEventListener('click', onPrevClick);
    controller.nextBtn?.addEventListener('click', onNextClick);
    controller.fpsBtn?.addEventListener('click', onFpsClick);
    controller.qualityBtn?.addEventListener('click', onQualityClick);
    controller.editInfo?.addEventListener('click', onMetaClick);

    controller.settingsMobileTrigger?.addEventListener(
        'click',
        onBSTriggerClick,
    );
    controller.bottomSheetClose?.addEventListener('click', onBSCloseClick);
    controller.mobilePlayOverlayBtn?.addEventListener(
        'click',
        onMobilePlayOverlayClick,
    );

    // Bottom Sheet Deep Navigation Setup with Cleanup Tracking
    const bsPanelRows = controller.bottomSheet?.querySelectorAll(
        '.Animation_bottomSheetRow',
    );
    bsPanelRows?.forEach((row) => {
        const handler = (e: Event) => {
            e.stopPropagation();
            const targetPanel = (row as HTMLElement).dataset.target;
            controller.bsMainPanel?.classList.remove('isActive');
            if (targetPanel === 'focus')
                controller.bsFocusPanel?.classList.add('isActive');
            if (targetPanel === 'fps')
                controller.bsFpsPanel?.classList.add('isActive');
            if (targetPanel === 'quality')
                controller.bsQualityPanel?.classList.add('isActive');
        };
        row.addEventListener('click', handler);
        dynamicCleanups.push(() => row.removeEventListener('click', handler));
    });

    const bsBackRows = controller.bottomSheet?.querySelectorAll(
        '.Animation_bottomSheetBackRow',
    );
    bsBackRows?.forEach((backRow) => {
        const handler = (e: Event) => {
            e.stopPropagation();
            controller.bsFocusPanel?.classList.remove('isActive');
            controller.bsFpsPanel?.classList.remove('isActive');
            controller.bsQualityPanel?.classList.remove('isActive');
            controller.bsMainPanel?.classList.add('isActive');
        };
        backRow.addEventListener('click', handler);
        dynamicCleanups.push(() =>
            backRow.removeEventListener('click', handler),
        );
    });

    const bsOptions = controller.bottomSheet?.querySelectorAll(
        '.Animation_bottomSheetOption',
    );
    bsOptions?.forEach((option) => {
        const handler = (e: Event) => {
            e.stopPropagation();
            const type = (option as HTMLElement).dataset.type;
            const val = (option as HTMLElement).dataset.value || 'auto';
            if (type === 'focus') {
                const activeIndex = Number(val);
                controller.engine.setAlignmentIndex(activeIndex);
                const textNode =
                    controller.focusBtn?.querySelector('.Animation_btnText');
                const optionNode = controller.focusPopup?.querySelector(
                    `[data-value="${val}"]`,
                );
                if (textNode && optionNode) {
                    textNode.textContent = optionNode.textContent?.trim() || '';
                }
            } else if (type === 'fps') {
                const targetFps =
                    val === 'auto'
                        ? activePlaybackMode.defaultFps
                        : Number(val);
                controller.engine.updateFps(targetFps);
                const textNode =
                    controller.fpsBtn?.querySelector('.Animation_btnText');
                if (textNode) textNode.textContent = `${targetFps} FPS`;
            } else if (type === 'quality') {
                let resolvedSize = val;
                if (val === 'auto')
                    resolvedSize = activePlaybackMode.defaultSize || 'm';
                controller.engine.changeResolution(resolvedSize);

                controller.syncPlaybackPostResolutionChange(
                    activePlaybackMode.modeType,
                );
            }

            bsOptions.forEach((opt) => opt.classList.remove('isActive'));
            option.classList.add('isActive');
            controller.closeBottomSheet();
        };
        option.addEventListener('click', handler);
        dynamicCleanups.push(() =>
            option.removeEventListener('click', handler),
        );
    });

    // Pointer Unification & Hybrid Engine Detection Infrastructure
    const onControlsPointerEnter = () => {
        controller.isMouseOverControls = true;
        controller.resetHUDTimer();
    };

    const onControlsPointerLeave = () => {
        controller.isMouseOverControls = false;
        controller.resetHUDTimer();
    };

    // Phase 4: Main Layout Interactions Setup & Restored Image Guard
    const initImg = controller.container?.querySelector(
        `#AnimationInitImg_${uniqueId}`,
    ) as HTMLImageElement;
    const canvasBox = controller.container?.querySelector(
        '.Animation_canvasBox',
    );

    const revealCanvasWhenStillReady = () => {
        if (canvasBox) {
            canvasBox.classList.add('isReady');
        }
    };

    if (initImg) {
        if (initImg.complete) {
            revealCanvasWhenStillReady();
        } else {
            initImg.addEventListener('load', revealCanvasWhenStillReady);
        }
    }

    const hasHoverSupport = (controller as any).hasTrueHover;
    const progressWrapper = controller.container?.querySelector(
        '.Animation_progressWrapper',
    );

    if (hasHoverSupport) {
        controller.container?.addEventListener(
            'mousemove',
            controller.resetHUDTimer,
        );
        controller.controlsHUD?.addEventListener(
            'pointerenter',
            onControlsPointerEnter,
        );
        controller.controlsHUD?.addEventListener(
            'pointerleave',
            onControlsPointerLeave,
        );
        progressWrapper?.addEventListener(
            'pointerenter',
            onControlsPointerEnter,
        );
        progressWrapper?.addEventListener(
            'pointerleave',
            onControlsPointerLeave,
        );
    }

    controller.container?.addEventListener(
        'click',
        controller.handleContainerInteraction,
    );
    controller.container?.addEventListener(
        'touchstart',
        controller.handleTouchStart,
        { passive: false },
    );
    controller.container?.addEventListener(
        'touchmove',
        controller.handleTouchMove,
        { passive: false },
    );
    controller.container?.addEventListener(
        'touchend',
        controller.handleTouchEnd,
        { passive: false },
    );
    document.addEventListener('click', controller.handleDocumentClickOutside);

    // Phase 5: Functional Unmount Destructor Lifecycle
    return () => {
        controller.playBtn?.removeEventListener('click', onPlayClick);
        controller.prevBtn?.removeEventListener('click', onPrevClick);
        controller.nextBtn?.removeEventListener('click', onNextClick);
        controller.focusBtn?.removeEventListener('click', onFocusClick);
        controller.fpsBtn?.removeEventListener('click', onFpsClick);
        controller.qualityBtn?.removeEventListener('click', onQualityClick);
        controller.editInfo?.removeEventListener('click', onMetaClick);

        controller.settingsMobileTrigger?.removeEventListener(
            'click',
            onBSTriggerClick,
        );
        controller.bottomSheetClose?.removeEventListener(
            'click',
            onBSCloseClick,
        );
        controller.mobilePlayOverlayBtn?.removeEventListener(
            'click',
            onMobilePlayOverlayClick,
        );

        // Clean up all dynamic bottom sheet row & option listeners
        dynamicCleanups.forEach((cleanup) => cleanup());

        if (hasHoverSupport) {
            controller.container?.removeEventListener(
                'mousemove',
                controller.resetHUDTimer,
            );
            controller.controlsHUD?.removeEventListener(
                'pointerenter',
                onControlsPointerEnter,
            );
            controller.controlsHUD?.removeEventListener(
                'pointerleave',
                onControlsPointerLeave,
            );
            progressWrapper?.removeEventListener(
                'pointerenter',
                onControlsPointerEnter,
            );
            progressWrapper?.removeEventListener(
                'pointerleave',
                onControlsPointerLeave,
            );
        }

        initImg?.removeEventListener('load', revealCanvasWhenStillReady);

        controller.container?.removeEventListener(
            'click',
            controller.handleContainerInteraction,
        );
        controller.container?.removeEventListener(
            'touchstart',
            controller.handleTouchStart,
        );
        controller.container?.removeEventListener(
            'touchmove',
            controller.handleTouchMove,
        );
        controller.container?.removeEventListener(
            'touchend',
            controller.handleTouchEnd,
        );
        document.removeEventListener(
            'click',
            controller.handleDocumentClickOutside,
        );

        controller.destroy();
    };
}
